/**
 * Form validation and UI utilities
 * Validation methods return i18n translation keys instead of hardcoded strings.
 * Consuming components resolve keys with t() before displaying.
 */

export class FormValidator {
  /**
   * Validate quotation form data
   * @param {Object} formData
   * @returns {Object} { isValid: boolean, errors: Object } - errors values are i18n keys
   */
  static validateQuotationForm(formData) {
    const errors = {};

    if (!formData.quotationName?.trim()) {
      errors.quotationName = 'validation:quotationForm.quotationNameRequired';
    } else if (formData.quotationName.length > 120) {
      errors.quotationName = 'validation:quotationForm.quotationNameMaxLength';
    }

    if (!formData.custAccount) {
      errors.custAccount = 'validation:quotationForm.custAccountRequired';
    } else if (formData.customerType === null) {
      // Value present but not resolved to a customer/prospect (e.g. ambiguous URL prefill)
      errors.custAccount = 'validation:quotationForm.custAccountNotResolved';
    }

    // Contact is now optional - no validation needed
    // Follow-up date is now optional - no validation needed
    // Expiry date is now optional - no validation needed

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  /**
   * Validate quotation line data
   * @param {Object} lineData
   * @returns {Object} { isValid: boolean, errors: Object } - errors values are i18n keys
   */
  static validateQuotationLine(lineData) {
    const errors = {};

    if (lineData.productType === 'Existente' && !lineData.existingProduct) {
      errors.existingProduct = 'validation:quotationLine.existingProductRequired';
    }

    if (lineData.productType === 'Fuera de catálogo' && !lineData.outOfCatalogProduct?.trim()) {
      errors.outOfCatalogProduct = 'validation:quotationLine.outOfCatalogRequired';
    } else if (lineData.outOfCatalogProduct?.length > 60) {
      errors.outOfCatalogProduct = 'validation:quotationLine.outOfCatalogMaxLength';
    }

    if (!lineData.salesQty || lineData.salesQty <= 0) {
      errors.salesQty = 'validation:quotationLine.validQuantityRequired';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
}

export class PricingCalculator {
  /**
   * Calculate net price based on tariff price and discount
   * @param {number} tariffPrice
   * @param {number} discount
   * @returns {number}
   */
  static calculateNetPrice(tariffPrice, discount) {
    return tariffPrice * (1 - discount/100);
  }

  /**
   * Calculate discount percentage based on tariff and net price
   * @param {number} tariffPrice
   * @param {number} netPrice
   * @returns {number}
   */
  static calculateDiscount(tariffPrice, netPrice) {
    return (1 - netPrice/tariffPrice) * 100;
  }

  /**
   * Calculate line amount based on quantity and price
   * @param {number} quantity
   * @param {number} netPrice
   * @returns {number}
   */
  static calculateLineAmount(quantity, netPrice) {
    return quantity * netPrice;
  }

  /**
   * Calculate variation percentage
   * @param {number} defaultNet
   * @param {number} currentNet
   * @returns {number}
   */
  static calculateVariation(defaultNet, currentNet) {
    return ((defaultNet - currentNet) / defaultNet) * 100;
  }
}
